"use client";
import { motion, useAnimation, useInView } from "motion/react";
import { useRef, useEffect } from "react";

// All line segments use ease:"linear" with duration = height / SPEED,
// so the line advances at a constant rate regardless of segment length.
const SPEED = 250; // px per second

interface Props {
  date?: string;
  header: string;
  description: string;
  type?: string;
  // null = no filter active, true = match (glow), false = no match (dim)
  filterMatch?: boolean | null;
  // Injected automatically by Timeline — do not set manually
  isFirst?: boolean;
  isLast?: boolean;
  index?: number;
  canAnimate?: boolean;
  onComplete?: (index: number) => void;
  isPreview?: boolean;
  onPreviewComplete?: () => void;
}

const TimelineItem = ({
  date,
  header,
  description,
  type,
  filterMatch = null,
  isFirst = false,
  isLast = false,
  index = 0,
  canAnimate = false,
  onComplete,
  isPreview = false,
  onPreviewComplete,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  // Refs to measure each flex-1 segment's actual rendered height
  const lineAboveEl = useRef<HTMLDivElement>(null);
  const lineBelowEl = useRef<HTMLDivElement>(null);

  const lineControls = useAnimation();
  const lineAboveControls = useAnimation();
  const dotControls = useAnimation();
  const contentControls = useAnimation();
  const lineBelowControls = useAnimation();

  // Track previous isPreview to detect true→false transition
  const prevIsPreviewRef = useRef(isPreview);

  // Initial entry animation
  useEffect(() => {
    if (!isInView || !canAnimate) return;
    const run = async () => {
      if (!isFirst) {
        // Connector gap: h-16 = 64px (fixed by CSS class)
        await lineControls.start({
          scaleY: 1,
          transition: { duration: 64 / SPEED, ease: "linear" },
        });
        // Line above dot: flex-1, height measured at runtime
        const lineAboveH = lineAboveEl.current?.offsetHeight ?? 50;
        await lineAboveControls.start({
          scaleY: 1,
          transition: { duration: lineAboveH / SPEED, ease: "linear" },
        });
      }

      if (isPreview) {
        // Preview: fade content to partial opacity, no dot, no line-below, no onComplete
        setTimeout(() => {
          contentControls.start({
            opacity: 0.45,
            y: 0,
            transition: { duration: 0.35, ease: "easeOut" },
          }).then(() => onPreviewComplete?.());
        }, 80);
        return;
      }

      // Normal: dot pops, content fades fully in
      dotControls.start({
        scale: 1,
        opacity: 1,
        transition: { type: "spring", stiffness: 400, damping: 22 },
      });

      setTimeout(() => {
        contentControls.start({
          opacity: 1,
          y: 0,
          transition: { duration: 0.35, ease: "easeOut" },
        });
      }, 80);

      if (!isLast) {
        const lineBelowH = lineBelowEl.current?.offsetHeight ?? 50;
        await lineBelowControls.start({
          scaleY: 1,
          transition: { duration: lineBelowH / SPEED, ease: "linear" },
        });
        onComplete?.(index);
      } else {
        onComplete?.(index);
      }
    };
    run();
  }, [isInView, canAnimate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transition: preview → full (fires when isPreview flips true→false)
  useEffect(() => {
    const wasPreview = prevIsPreviewRef.current;
    prevIsPreviewRef.current = isPreview;
    if (!wasPreview || isPreview) return;

    const transition = async () => {
      // Dot springs in, content transitions to full opacity — both non-blocking
      dotControls.start({
        scale: 1,
        opacity: 1,
        transition: { type: "spring", stiffness: 400, damping: 22 },
      });
      contentControls.start({
        opacity: 1,
        transition: { duration: 0.4, ease: "easeOut" },
      });

      if (!isLast) {
        await new Promise<void>((r) => setTimeout(r, 80));
        const lineBelowH = lineBelowEl.current?.offsetHeight ?? 50;
        await lineBelowControls.start({
          scaleY: 1,
          transition: { duration: lineBelowH / SPEED, ease: "linear" },
        });
        onComplete?.(index);
      } else {
        setTimeout(() => onComplete?.(index), 400);
      }
    };
    transition();
  }, [isPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={ref}>
      {/* Connector line between items */}
      {!isFirst && (
        <div className="flex flex-row">
          <div className="w-16 flex-none flex flex-col items-center">
            <motion.div
              initial={{ scaleY: 0 }}
              animate={lineControls}
              className="w-0.5 h-16 bg-(--uforeground2)"
              style={{ transformOrigin: "top" }}
            />
          </div>
        </div>
      )}

      {/* Item row */}
      <div className="flex flex-row relative">
        <div className="w-16 flex-none flex flex-col items-center">
          {!isFirst ? (
            <motion.div
              ref={lineAboveEl}
              initial={{ scaleY: 0 }}
              animate={lineAboveControls}
              className="w-0.5 flex-1 bg-(--uforeground2)"
              style={{ transformOrigin: "top" }}
            />
          ) : (
            <div className="flex-1" />
          )}
          <div className="h-4 w-4 flex-none" />
          {!isLast ? (
            <motion.div
              ref={lineBelowEl}
              initial={{ scaleY: 0 }}
              animate={lineBelowControls}
              className="w-0.5 flex-1 bg-(--uforeground2)"
              style={{ transformOrigin: "top" }}
            />
          ) : (
            <div className="flex-1" />
          )}
        </div>

        {/* Dot */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={dotControls}
          className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-(--uforeground2) border-2 border-(--background) z-10"
        />

        {/* Content card */}
        <div
          className="flex-1 ml-4"
          style={{
            boxShadow: filterMatch === true ? "0 0 28px 6px var(--filter-glow-color)" : "0 0 0px 0px transparent",
            opacity: filterMatch === false ? 0.3 : 1,
            transition: "opacity 0.5s ease-out, box-shadow 0.5s ease-out",
            borderRadius: "16px",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={contentControls}
            className="bg-(--background2) rounded-[16px] p-6 border border-(--uforeground2)"
          >
            {date && <p className="text-sm text-(--uforeground) mb-1">{date}</p>}
            <h2 className="text-2xl font-semibold mb-2">{header}</h2>
            <p className="text-(--foreground2) whitespace-pre-line">
              {description}
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TimelineItem;
